import { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { artworkService, studentService, tagService } from '../../services';
import { LoadingSkeleton, ConfirmDialog, InlineError } from '../ui';
import { useToast, ToastProvider } from '../ui/Toast';
import { useDebouncedValue } from '../../hooks';
import EditArtworkModal from './EditArtworkModal';
import { ArtworkFilters } from './ArtworkFilters';
import { ArtworkGrid } from './ArtworkGrid';
import { ArtworkBulkActions } from './ArtworkBulkActions';
import type { ArtworkFilters as ArtworkFiltersType } from '../../services/artworkService';
import { EmptyState as BaseEmptyState } from './shared/AdminPrimitives';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';

// Types
interface ArtworkManagementState {
  viewMode: 'grid' | 'list';
  selectedArtworks: Set<number>;
  showCheckboxes: boolean;
  filters: ArtworkFiltersType;
  sortBy: 'uploaded_at' | 'student_name' | 'tag_count';
  sortDirection: 'asc' | 'desc';
  searchQuery: string;
  currentPage: number;
  showImageModal: number | null;
  showEditModal: number | null;
  showDeleteConfirm: number | null;
  showBulkDeleteConfirm: boolean;
}

// Styled Components
const ManagementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
  min-height: 100vh;
`;

const Header = styled.header`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const HeaderTitle = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.gray.light};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-width: 120px;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.gray.medium};
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionLink = styled(Link)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
    transform: translateY(-1px);
  }
`;

const ViewControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const ViewToggleGroup = styled.div`
  display: flex;
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const ViewToggleButton = styled.button<{ active: boolean }>`
  background: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.white};
  color: ${({ active, theme }) => active ? theme.colors.white : theme.colors.text};
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:hover {
    background: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.gray.light};
  }
`;

const SortControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const FilterLabel = styled.label`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContentArea = styled.section`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: visible;
`;

const EmptyState = styled(BaseEmptyState)`
  padding: ${({ theme }) => theme.spacing['3xl']};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const EmptyMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  margin-right: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.gray.light};
`;

const PaginationButton = styled.button`
  background: none;
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ModalOverlay = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  opacity: ${({ show }) => show ? 1 : 0};
  visibility: ${({ show }) => show ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.transitions.normal};
`;

const ModalContent = styled.div`
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
`;

const ModalImage = styled(ArtworkObjectImage)`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
`;

const SkeletonGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
`;

export default function ArtworkManagement() {
  const queryClient = useQueryClient();
  const { toasts, removeToast, showError, showSuccess } = useToast();

  // State management
  const [state, setState] = useState<ArtworkManagementState>({
    viewMode: 'grid',
    selectedArtworks: new Set(),
    showCheckboxes: false,
    filters: {},
    sortBy: 'uploaded_at',
    sortDirection: 'desc',
    searchQuery: '',
    currentPage: 1,
    showImageModal: null,
    showEditModal: null,
    showDeleteConfirm: null,
    showBulkDeleteConfirm: false,
  });

  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [hoveredArtwork, setHoveredArtwork] = useState<number | null>(null);

  const debouncedSearchQuery = useDebouncedValue(state.searchQuery, 300);

  // Data fetching
  const { data: artworkData, isLoading: loadingArtworks, error: artworkError } = useQuery({
    queryKey: ['admin', 'artworks', state.currentPage, state.filters, state.sortBy, state.sortDirection, debouncedSearchQuery],
    queryFn: () => artworkService.getAdminArtworks(
      state.currentPage,
      20,
      {
        ...state.filters,
        search: debouncedSearchQuery || undefined,
      }
    ),
    staleTime: 5 * 60 * 1000,
  });

  const { data: students = [] } = useQuery({
    queryKey: ['admin', 'students-list'],
    queryFn: () => studentService.getStudents(1, 100),
    select: (data) => data.students,
    staleTime: 10 * 60 * 1000,
  });

  const { data: tagsData } = useQuery({
    queryKey: ['admin', 'tags-list'],
    queryFn: () => tagService.getAdminTags(),
    staleTime: 10 * 60 * 1000,
  });
  const tags = tagsData?.tags || [];

  const { data: uploadStats } = useQuery({
    queryKey: ['admin', 'artwork-stats'],
    queryFn: () => artworkService.getUploadStats(),
    staleTime: 5 * 60 * 1000,
  });

  // Mutations
  const deleteArtworkMutation = useMutation({
    mutationFn: artworkService.deleteArtwork,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'artworks'] });
      setState(prev => ({ ...prev, showDeleteConfirm: null }));
      showSuccess('Artwork deleted successfully');
    },
    onError: (error: any) => {
      showError(error.message || 'Failed to delete artwork. Please try again.');
      setState(prev => ({ ...prev, showDeleteConfirm: null }));
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (artworkIds: number[]) => {
      await Promise.all(artworkIds.map(id => artworkService.deleteArtwork(id)));
    },
    onSuccess: (_, artworkIds) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'artworks'] });
      setState(prev => ({
        ...prev,
        selectedArtworks: new Set(),
        showCheckboxes: false,
        showBulkDeleteConfirm: false
      }));
      showSuccess(`Successfully deleted ${artworkIds.length} artwork${artworkIds.length !== 1 ? 's' : ''}`);
    },
    onError: (error: any) => {
      showError(error.message || 'Failed to delete artworks. Please try again.');
      setState(prev => ({ ...prev, showBulkDeleteConfirm: false }));
    },
  });

  const rotateArtworkMutation = useMutation({
    mutationFn: ({ artworkId, direction }: { artworkId: number, direction: 'clockwise' | 'counterclockwise' }) => {
      return artworkService.rotateArtwork(artworkId, direction);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'artworks'] });
      queryClient.refetchQueries({ queryKey: ['admin', 'artworks'] });
      showSuccess('Artwork rotated successfully');
    },
    onError: (error: any) => {
      showError(error.message || 'Failed to rotate artwork. Please try again.');
    },
  });

  // Computed values
  const artworks = useMemo(() => artworkData?.artworks || [], [artworkData?.artworks]);
  const totalArtworks = artworkData?.total || 0;
  const totalPages = artworkData?.totalPages || 1;

  const stats = useMemo(() => ({
    total: totalArtworks,
    thisMonth: uploadStats?.uploads_this_month ?? 0,
    selected: state.selectedArtworks.size,
    hasFilters: Object.keys(state.filters).length > 0 || debouncedSearchQuery.length > 0,
  }), [uploadStats?.uploads_this_month, totalArtworks, state.selectedArtworks.size, state.filters, debouncedSearchQuery]);

  // Event handlers
  const updateState = useCallback((updates: Partial<ArtworkManagementState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    updateState({ searchQuery: value, currentPage: 1 });
  }, [updateState]);

  const handleFilterChange = useCallback((filterKey: keyof ArtworkFiltersType, value: ArtworkFiltersType[keyof ArtworkFiltersType]) => {
    updateState({
      filters: { ...state.filters, [filterKey]: value || undefined },
      currentPage: 1
    });
  }, [updateState, state.filters]);

  const clearFilters = useCallback(() => {
    updateState({
      filters: {},
      searchQuery: '',
      currentPage: 1,
      selectedArtworks: new Set(),
      showCheckboxes: false
    });
  }, [updateState]);

  const handleArtworkSelect = useCallback((artworkId: number, selected: boolean) => {
    const newSelected = new Set(state.selectedArtworks);
    if (selected) {
      newSelected.add(artworkId);
    } else {
      newSelected.delete(artworkId);
    }
    updateState({
      selectedArtworks: newSelected,
      showCheckboxes: newSelected.size > 0
    });
  }, [updateState, state.selectedArtworks]);

  const handleSelectAll = useCallback(() => {
    const newSelected = state.selectedArtworks.size === artworks.length
      ? new Set<number>()
      : new Set(artworks.map(a => a.artwork_id));
    updateState({
      selectedArtworks: newSelected,
      showCheckboxes: newSelected.size > 0
    });
  }, [updateState, state.selectedArtworks.size, artworks]);

  const handleRotateArtwork = useCallback((artworkId: number, direction: 'clockwise' | 'counterclockwise') => {
    rotateArtworkMutation.mutate({ artworkId, direction });
    setActiveMenu(null);
  }, [rotateArtworkMutation]);

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStudentName = (studentId?: number | null) => {
    if (!studentId) return 'Unknown Student';
    const student = students.find(s => s.student_id === studentId);
    if (!student) return 'Unknown Student';

    return student.name || 'Unknown Student';
  };

  if (artworkError) {
    return (
      <ManagementContainer>
        <InlineError
          error="Failed to load artworks. Please try again."
          onDismiss={() => queryClient.invalidateQueries({ queryKey: ['admin', 'artworks'] })}
        />
      </ManagementContainer>
    );
  }

  return (
    <>
      <ToastProvider toasts={toasts} onRemove={removeToast} />
      <ManagementContainer>
        {/* Header Section */}
        <Header>
          <HeaderTitle>Artwork Management</HeaderTitle>

          <StatsRow>
            <StatCard>
              <StatNumber>{stats.total}</StatNumber>
              <StatLabel>Total Artworks</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{stats.thisMonth}</StatNumber>
              <StatLabel>This Month</StatLabel>
            </StatCard>
            {state.selectedArtworks.size > 0 && (
              <StatCard>
                <StatNumber>{stats.selected}</StatNumber>
                <StatLabel>Selected</StatLabel>
              </StatCard>
            )}
          </StatsRow>

          <QuickActions>
            <ActionLink to="/admin/upload">
              Upload Artwork
            </ActionLink>
            <ActionLink to="/admin/students">
              Manage Students
            </ActionLink>
            <ActionLink to="/admin/exhibitions">
              Create Exhibition
            </ActionLink>
          </QuickActions>
        </Header>

        {/* Filters Section */}
        <ArtworkFilters
          filters={state.filters}
          searchQuery={state.searchQuery}
          students={students}
          tags={tags}
          hasFilters={stats.hasFilters}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
        />

        {/* View Controls */}
        <ViewControls>
          <ViewToggleGroup>
            <ViewToggleButton
              active={state.viewMode === 'grid'}
              onClick={() => updateState({ viewMode: 'grid' })}
            >
              Grid
            </ViewToggleButton>
            <ViewToggleButton
              active={state.viewMode === 'list'}
              onClick={() => updateState({ viewMode: 'list' })}
            >
              List
            </ViewToggleButton>
          </ViewToggleGroup>

          <SortControls>
            <FilterLabel>Sort by:</FilterLabel>
            <FilterSelect
              value={state.sortBy}
              onChange={(e) => updateState({ sortBy: e.target.value as 'uploaded_at' | 'student_name' | 'tag_count' })}
            >
              <option value="uploaded_at">Upload Date</option>
              <option value="student_name">Student Name</option>
              <option value="tag_count">Tag Count</option>
            </FilterSelect>
            <ActionButton
              onClick={() => updateState({
                sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc'
              })}
            >
              {state.sortDirection === 'asc' ? '↑' : '↓'}
            </ActionButton>
          </SortControls>

          {state.showCheckboxes && artworks.length > 0 && (
            <div>
              <Checkbox
                checked={state.selectedArtworks.size === artworks.length}
                onChange={handleSelectAll}
                aria-label={`Select all ${artworks.length} artworks`}
              />
              Select All ({state.selectedArtworks.size} of {artworks.length})
            </div>
          )}
        </ViewControls>

        {/* Content Area */}
        <ContentArea>
          {loadingArtworks ? (
            <SkeletonGrid>
              {Array.from({ length: 8 }).map((_, i) => (
                <LoadingSkeleton key={i} height="200px" />
              ))}
            </SkeletonGrid>
          ) : artworks.length === 0 ? (
            <EmptyState>
              <EmptyIcon>🎨</EmptyIcon>
              <EmptyTitle>
                {stats.hasFilters ? 'No artworks match your filters' : 'No artworks uploaded yet'}
              </EmptyTitle>
              <EmptyMessage>
                {stats.hasFilters
                  ? 'Try adjusting your search criteria or clearing filters.'
                  : 'Start by uploading some student artwork to get started.'
                }
              </EmptyMessage>
              {!stats.hasFilters && (
                <ActionLink to="/admin/upload">
                  Upload First Artwork
                </ActionLink>
              )}
            </EmptyState>
          ) : (
            <>
              <ArtworkGrid
                artworks={artworks}
                viewMode={state.viewMode}
                selectedArtworks={state.selectedArtworks}
                showCheckboxes={state.showCheckboxes}
                hoveredArtwork={hoveredArtwork}
                activeMenu={activeMenu}
                onHover={setHoveredArtwork}
                onSelect={handleArtworkSelect}
                onMenuToggle={setActiveMenu}
                onViewImage={(id) => updateState({ showImageModal: id })}
                onEditArtwork={(id) => updateState({ showEditModal: id })}
                onDeleteArtwork={(id) => updateState({ showDeleteConfirm: id })}
                onRotateArtwork={handleRotateArtwork}
                getStudentName={getStudentName}
                formatRelativeDate={formatRelativeDate}
              />

              {totalPages > 1 && (
                <Pagination>
                  <PaginationButton
                    disabled={state.currentPage === 1}
                    onClick={() => updateState({ currentPage: state.currentPage - 1 })}
                  >
                    Previous
                  </PaginationButton>
                  <PageInfo>Page {state.currentPage} of {totalPages}</PageInfo>
                  <PaginationButton
                    disabled={state.currentPage === totalPages}
                    onClick={() => updateState({ currentPage: state.currentPage + 1 })}
                  >
                    Next
                  </PaginationButton>
                </Pagination>
              )}
            </>
          )}
        </ContentArea>

        {/* Bulk Actions Bar */}
        <ArtworkBulkActions
          selectedCount={state.selectedArtworks.size}
          onTagSelected={() => updateState({ showEditModal: Array.from(state.selectedArtworks)[0] })}
          onDeleteSelected={() => updateState({ showBulkDeleteConfirm: true })}
        />

        {/* Image Modal */}
        <ModalOverlay
          show={state.showImageModal !== null}
          onClick={() => updateState({ showImageModal: null })}
        >
          {state.showImageModal && (
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={() => updateState({ showImageModal: null })}>X</CloseButton>
              <ModalImage
                artworkId={state.showImageModal}
                variant="image"
                access="admin"
                updatedAt={artworks.find(a => a.artwork_id === state.showImageModal)?.updated_at}
                lazy={false}
                alt="Full size artwork"
              />
            </ModalContent>
          )}
        </ModalOverlay>

        {/* Edit Artwork Modal */}
        <EditArtworkModal
          isOpen={state.showEditModal !== null}
          artworkId={state.showEditModal}
          artworkIds={state.selectedArtworks.size > 1 ? Array.from(state.selectedArtworks) : undefined}
          currentTags={
            state.showEditModal
              ? artworks.find(a => a.artwork_id === state.showEditModal)?.tags || []
              : []
          }
          studentId={
            state.showEditModal
              ? artworks.find(a => a.artwork_id === state.showEditModal)?.student_id
              : null
          }
          onClose={() => setState(prev => ({ ...prev, showEditModal: null }))}
          onSuccess={() => {
            if (state.selectedArtworks.size > 0) {
              updateState({
                selectedArtworks: new Set(),
                showCheckboxes: false
              });
            }
          }}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={state.showDeleteConfirm !== null}
          title="Delete Artwork"
          message="Are you sure you want to delete this artwork? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={() => {
            if (state.showDeleteConfirm) {
              deleteArtworkMutation.mutate(state.showDeleteConfirm);
            }
          }}
          onCancel={() => updateState({ showDeleteConfirm: null })}
          loading={deleteArtworkMutation.isPending}
        />

        {/* Bulk Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={state.showBulkDeleteConfirm}
          title="Delete Selected Artworks"
          message={`Are you sure you want to delete ${state.selectedArtworks.size} artwork${state.selectedArtworks.size !== 1 ? 's' : ''}? This action cannot be undone.`}
          confirmText={`Delete ${state.selectedArtworks.size} Artwork${state.selectedArtworks.size !== 1 ? 's' : ''}`}
          cancelText="Cancel"
          variant="danger"
          onConfirm={() => bulkDeleteMutation.mutate(Array.from(state.selectedArtworks))}
          onCancel={() => updateState({ showBulkDeleteConfirm: false })}
          loading={bulkDeleteMutation.isPending}
        />
      </ManagementContainer>
    </>
  );
}
